import { Docket, DocketItemStatus } from '@oolio-group/domain';
import { Order, OrbOrderStatus } from '@/types/orderTypes';
import { APP_CONFIG } from '@/config';

/**
 * Maps DocketItem status to ORB display status
 */
const docketStatusToOrbStatus = {
  CREATED: OrbOrderStatus.PREPARING,
  COMPLETED: OrbOrderStatus.READY,
  VOIDED: OrbOrderStatus.ELAPSED, // Voided orders should be hidden/elapsed
  BUMPED: OrbOrderStatus.READY, // Bumped means ready for pickup
  RECALLED: OrbOrderStatus.PREPARING, // Recalled back to kitchen
  SERVED: OrbOrderStatus.ELAPSED, // Served orders should be hidden/elapsed
  REBUMPED: OrbOrderStatus.READY, // Re-bumped means ready again
  TRANSFERRED: OrbOrderStatus.PREPARING, // Transferred to another station
} as const satisfies Record<DocketItemStatus, OrbOrderStatus>;

/**
 * Determines the aggregated order status from multiple dockets
 *
 * @param dockets - Array of dockets to aggregate
 * @returns Aggregated ORB order status
 */
export const getAggregatedOrderStatus = (dockets: Docket[]): OrbOrderStatus => {
  // Map each docket status to ORB status
  const docketOrbStatuses = dockets.map(
    docket =>
      docketStatusToOrbStatus[docket.itemStatus] ?? OrbOrderStatus.PREPARING,
  );

  // Order aggregation logic:
  // - If ALL dockets map to ELAPSED → ORB order status = 'ELAPSED' (hidden)
  // - Otherwise, ignoring ELAPSED: if ALL remaining map to READY → 'READY'
  // - Otherwise → 'PREPARING' (at least one PREPARING present)

  const allElapsed = docketOrbStatuses.every(
    status => status === OrbOrderStatus.ELAPSED,
  );

  if (allElapsed) {
    return OrbOrderStatus.ELAPSED;
  }

  // Filter out ELAPSED dockets for aggregation
  const activeStatuses = docketOrbStatuses.filter(
    status => status !== OrbOrderStatus.ELAPSED,
  );

  // All active dockets are READY
  if (activeStatuses.every(status => status === OrbOrderStatus.READY)) {
    return OrbOrderStatus.READY;
  }
  return OrbOrderStatus.PREPARING;
};

/**
 * Aggregates multiple dockets into a single order with computed status and auto-clear logic
 *
 * An order can have multiple dockets (one per kitchen station/item).
 * The ORB order status is determined by aggregating all dockets for that order:
 * - Each docket status is mapped to an ORB status using docketStatusToOrbStatus
 * - If ALL dockets map to READY/ELAPSED → ORB order status = 'READY' (shows in "Ready!" column)
 * - If ANY docket maps to PREPARING → ORB order status = 'PREPARING' (shows in "Preparing..." column)
 * - If order is READY and has exceeded timeout → ORB order status = 'ELAPSED' (hidden from display)
 *
 * @param dockets - Array of dockets from the API response
 * @returns Array of aggregated orders with computed status and auto-clear logic applied
 */
export const aggregateDocketsToOrders = ({
  dockets,
  getSalesChannelName,
  timeoutMs,
}: {
  dockets: Docket[];
  getSalesChannelName: (id: string) => string | undefined;
  timeoutMs: number;
}): Order[] => {
  // Group dockets by orderId since one order can have multiple dockets
  const docketsByOrder = dockets.reduce((acc, docket) => {
    const orderId = docket.orderId;
    if (!acc[orderId]) {
      acc[orderId] = [];
    }
    acc[orderId].push(docket);
    return acc;
  }, {} as Record<string, Docket[]>);

  // Transform grouped dockets to ORB orders with aggregated status
  return Object.entries(docketsByOrder).map(([_, dockets]) => {
    // Determine aggregated order status from all dockets
    const aggregatedStatus = getAggregatedOrderStatus(dockets);

    // Use first docket for order details (they should be the same across dockets)
    const firstDocket = dockets[0];

    // Calculate order.updatedTime from docket.updatedTime fields
    // Single docket: use docket.updatedTime
    // Multiple dockets: use Math.max(...dockets.map(d => d.updatedTime))
    const orderUpdatedTime =
      dockets.length === 1
        ? firstDocket.updatedTime
        : Math.max(...dockets.map(docket => docket.updatedTime));

    // Auto-clear timeout logic: Mark expired Ready orders as COMPLETED
    // This prevents old ready orders from cluttering the display
    const now = Date.now();

    let finalStatus: OrbOrderStatus = aggregatedStatus;
    if (aggregatedStatus === OrbOrderStatus.READY) {
      const timeElapsedSinceUpdate = now - orderUpdatedTime;
      if (timeElapsedSinceUpdate >= timeoutMs) {
        // Mark as ELAPSED (will be filtered out from display in useDockets)
        finalStatus = OrbOrderStatus.ELAPSED;
      }
    }

    const order: Order = {
      id: firstDocket.orderId, // Use the actual order ID
      isOnline: firstDocket.isOnline,
      orderNumber: firstDocket.orderNumber,
      customerName: firstDocket.customer?.name?.trim(),
      customerIdentifier:
        firstDocket.customer?.phone || firstDocket.customer?.id,
      status: finalStatus,
      orderType: firstDocket.orderType,
      orderTypeName: firstDocket.orderType?.name?.trim(),
      tableName: firstDocket.table?.name?.trim(),
      tokenNumber: firstDocket.tokenNumber,
      salesChannelName: getSalesChannelName(firstDocket.salesChannel?.id),
      createdAt: firstDocket.createdAt,
      updateTime: orderUpdatedTime,
    };
    return order;
  });
};

export const filterDocketsWithinConfiguredTimeframe = (
  dockets: Docket[],
): Docket[] => {
  const filterHoursInMs = APP_CONFIG.DOCKET_FILTER_HOURS * 60 * 60 * 1000;
  const cutoffTime = Date.now() - filterHoursInMs;
  return dockets.filter(docket => docket.createdAt >= cutoffTime);
};

export const filterOrdersByType = (
  orders: Order[],
  orderTypes: string[],
): Order[] => {
  return orders.filter(order => {
    if (!order.orderType?.id) {
      return false;
    }
    return orderTypes.includes(order.orderType.id);
  });
};
